import { Participant, Tournament, TournamentStatus, TournamentType } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { AdBanner, PatternBackground, ShareQRModal } from '@/src/presentation/components';
import { FontAwesome5, Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useState } from 'react';
import { ActivityIndicator, FlatList, ScrollView, Text, TouchableOpacity, View } from 'react-native';

function DescriptionText({ description, color, primaryColor }: { description: string; color: string; primaryColor: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isLong = description.split('\n').length > 4 || description.length > 200;
  return (
    <View>
      <View style={!expanded ? { maxHeight: 88, overflow: 'hidden' } : undefined}>
        <Text numberOfLines={expanded ? undefined : 4} ellipsizeMode="tail" style={{ color, fontSize: 14, lineHeight: 22 }}>
          {description}
        </Text>
      </View>
      {isLong && (
        <TouchableOpacity onPress={() => setExpanded(!expanded)} style={{ marginTop: 4 }}>
          <Text style={{ color: primaryColor, fontSize: 14 }}>{expanded ? t('common.seeLess') : t('common.seeMore')}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}
import { createStyles } from './PublicTournamentScreen.styles';

export interface PublicTournamentScreenProps {
  tournament: Tournament;
  participants: Participant[];
  champion: Participant | null;
  shareUrl: string;
  isLoggedIn: boolean;
  isFollowing: boolean;
  followerCount: number;
  followLoading: boolean;
  handleViewBracket: () => void;
  handleParticipantPress: (userId: string) => void;
  handleLocationPress: () => void;
  handleFollowToggle: () => void;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
    overlay: string;
    badgeSetup: string;
    badgeProgress: string;
    badgeCompleted: string;
  };
}

export const PublicTournamentScreen = ({
  tournament,
  participants,
  champion,
  shareUrl,
  isLoggedIn,
  isFollowing,
  followerCount,
  followLoading,
  handleViewBracket,
  handleParticipantPress,
  handleLocationPress,
  handleFollowToggle,
  colors,
}: PublicTournamentScreenProps) => {
  const styles = createStyles();
  const { t, language } = useTranslation();
  const [qrModalVisible, setQrModalVisible] = useState(false);

  const getStatusLabel = (status: TournamentStatus) => {
    switch (status) {
      case 'setup': return t('tournaments.setup');
      case 'in_progress': return t('tournaments.inProgress');
      case 'completed': return t('tournaments.completed');
      default: return status;
    }
  };

  const getTournamentTypeLabel = (type: TournamentType) => {
    switch (type) {
      case 'single_elimination': return t('tournaments.singleElimination');
      case 'double_elimination': return t('tournaments.doubleElimination');
      case 'round_robin': return t('tournaments.roundRobin');
      case 'swiss': return t('tournaments.swiss');
      default: return type;
    }
  };

  const getParticipantCountText = (count: number, max?: number) => {
    const countText = max ? `${count} / ${max}` : `${count}`;
    const label = count === 1 ? t('tournaments.participant') : t('tournaments.participants');
    return `${countText} ${label.toLowerCase()}`;
  };

  const getFollowerCountText = (count: number) => {
    const label = count === 1 ? t('followers.follower') : t('followers.followers');
    return `${count} ${label}`;
  };

  const getDateLocale = () => language === 'es' ? 'es-ES' : 'en-US';

  const renderParticipant = ({ item }: { item: Participant }) => {
    const isRegistered = !!item.userId;

    return (
      <TouchableOpacity
        style={[styles.participantItem, { backgroundColor: colors.card, borderColor: colors.border }]}
        onPress={() => isRegistered && item.userId ? handleParticipantPress(item.userId) : null}
        activeOpacity={isRegistered ? 0.7 : 1}
        disabled={!isRegistered}
      >
        <Text style={[styles.seedBadge, { backgroundColor: colors.primary }]}>#{item.seed}</Text>
        <View style={styles.participantDetails}>
          <View style={styles.participantNameRow}>
            <Text style={[styles.participantName, { color: colors.text }]}>{item.name}</Text>
            {item.userId && (
              <Ionicons name="person-circle" size={16} color={colors.primary} style={{ marginLeft: 6 }} />
            )}
          </View>
          {item.userId && item.userEmail && (
            <Text style={[styles.participantEmail, { color: colors.textSecondary }]}>{item.userEmail}</Text>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <PatternBackground>
      <ScrollView style={[styles.scrollContent, { backgroundColor: 'transparent' }]} contentContainerStyle={{ paddingBottom: 24 }}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerBadgeRow}>
          <View style={[styles.publicBadge, { backgroundColor: colors.primary }]}>
            <Ionicons name="globe-outline" size={14} color="#fff" style={{ marginRight: 6 }} />
            <Text style={styles.publicBadgeText}>{t('tournaments.publicTournament')}</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {isLoggedIn && (
              <TouchableOpacity
                style={[
                  styles.followButton,
                  {
                    backgroundColor: isFollowing ? colors.card : colors.primary,
                    borderColor: isFollowing ? colors.border : colors.primary,
                  },
                ]}
                onPress={handleFollowToggle}
                disabled={followLoading}
              >
                {followLoading ? (
                  <ActivityIndicator size="small" color={isFollowing ? colors.primary : colors.primaryText} />
                ) : (
                  <>
                    <Ionicons
                      name={isFollowing ? 'heart' : 'heart-outline'}
                      size={16}
                      color={isFollowing ? colors.primary : colors.primaryText}
                      style={{ marginRight: 4 }}
                    />
                    <Text
                      style={[
                        styles.followButtonText,
                        { color: isFollowing ? colors.primary : colors.primaryText },
                      ]}
                    >
                      {isFollowing ? t('tournaments.following') : t('tournaments.follow')}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.qrButton, { backgroundColor: colors.card, borderColor: colors.border }]}
              onPress={() => setQrModalVisible(true)}
            >
              <Ionicons name="qr-code-outline" size={20} color={colors.primary} />
            </TouchableOpacity>
          </View>
        </View>

        <Text style={[styles.title, { color: colors.text }]}>{tournament.name}</Text>

        {tournament.description && (
          <DescriptionText description={tournament.description} color={colors.textSecondary} primaryColor={colors.primary} />
        )}

        <View style={styles.infoRow}>
          <View style={styles.tournamentTypeRow}>
            {tournament.tournamentType === 'single_elimination' && (
              <>
                <FontAwesome5 name="trophy" size={14} color={colors.primary} />
                <Text style={[styles.infoText, { color: colors.primary }]}>{getTournamentTypeLabel('single_elimination')}</Text>
              </>
            )}
            {tournament.tournamentType === 'double_elimination' && (
              <>
                <MaterialCommunityIcons name="tournament" size={16} color={colors.primary} />
                <Text style={[styles.infoText, { color: colors.primary }]}>{getTournamentTypeLabel('double_elimination')}</Text>
              </>
            )}
            {tournament.tournamentType === 'round_robin' && (
              <>
                <MaterialCommunityIcons name="sync-circle" size={16} color={colors.primary} />
                <Text style={[styles.infoText, { color: colors.primary }]}>{getTournamentTypeLabel('round_robin')}</Text>
              </>
            )}
            {tournament.tournamentType === 'swiss' && (
              <>
                <MaterialCommunityIcons name="grid" size={16} color={colors.primary} />
                <Text style={[styles.infoText, { color: colors.primary }]}>{getTournamentTypeLabel('swiss')}</Text>
              </>
            )}
          </View>
          <View
            style={[
              styles.statusBadge,
              {
                backgroundColor:
                  tournament.status === 'completed'
                    ? colors.badgeCompleted
                    : tournament.status === 'in_progress'
                      ? colors.badgeProgress
                      : colors.badgeSetup,
              },
            ]}
          >
            <Text style={[styles.statusText, { color: colors.text }]}>{getStatusLabel(tournament.status)}</Text>
          </View>
        </View>

        {champion && (
          <View style={[styles.championBanner, { borderColor: 'rgba(184, 134, 11, 0.3)', backgroundColor: 'rgba(184, 134, 11, 0.08)' }]}>
            <FontAwesome5 name="trophy" size={12} color="#B8860B" style={{ marginRight: 6 }} />
            <Text style={styles.championLabel}>{t('tournaments.champion').toUpperCase()}  </Text>
            <Text style={[styles.championName, { color: colors.text }]}>{champion.name}</Text>
          </View>
        )}

        <View style={styles.statsRow}>
          <Text style={[styles.participantCount, { color: colors.textSecondary }]}>
            {getParticipantCountText(participants.length, tournament.maxParticipants)}
          </Text>
          <Text style={[styles.followerCount, { color: colors.textSecondary }]}>
            <Ionicons name="heart" size={12} color={colors.textSecondary} /> {getFollowerCountText(followerCount)}
          </Text>
        </View>

        {/* Location & Date Info */}
        {(tournament.location || tournament.startDate) && (
          <View style={[styles.eventInfoSection, { borderTopColor: colors.border }]}>
            {tournament.startDate && (
              <View style={styles.eventInfoRow}>
                <Ionicons name="calendar-outline" size={20} color={colors.textSecondary} style={styles.eventInfoIcon} />
                <View style={styles.eventInfoText}>
                  <Text style={[styles.eventInfoLabel, { color: colors.textSecondary }]}>{t('tournaments.dateTime')}</Text>
                  <Text style={[styles.eventInfoValue, { color: colors.text }]}>
                    {new Date(tournament.startDate).toLocaleString(getDateLocale(), {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                </View>
              </View>
            )}

            {tournament.location && (
              <TouchableOpacity
                style={styles.eventInfoRow}
                onPress={handleLocationPress}
                activeOpacity={tournament.locationLat && tournament.locationLng ? 0.7 : 1}
              >
                <Ionicons name="location-outline" size={20} color={colors.textSecondary} style={styles.eventInfoIcon} />
                <View style={styles.eventInfoText}>
                  <Text style={[styles.eventInfoLabel, { color: colors.textSecondary }]}>{t('tournaments.location')}</Text>
                  <Text
                    style={[
                      styles.eventInfoValue,
                      { color: tournament.locationLat ? colors.primary : colors.text },
                    ]}
                  >
                    {tournament.location}
                    {tournament.locationLat && tournament.locationLng && (
                      <Ionicons name="chevron-forward" size={16} color={colors.primary} style={{ marginLeft: 4 }} />
                    )}
                  </Text>
                </View>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>

        {/* Rules Section */}
        {tournament.rules && (
          <View style={[styles.rulesSection, { backgroundColor: colors.surface, borderColor: colors.border }]}>
            <View style={styles.rulesTitleRow}>
              <Ionicons name="clipboard-outline" size={18} color={colors.primary} />
              <Text style={[styles.rulesSectionTitle, { color: colors.primary }]}>{t('tournaments.tournamentRules')}</Text>
            </View>
            <Text style={[styles.rulesText, { color: colors.text }]}>{tournament.rules}</Text>
          </View>
        )}

        {/* Participants List */}
        <View style={styles.participantsSection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>{t('tournaments.participants')}</Text>

          <FlatList
            data={participants}
            keyExtractor={(item) => item.id}
            renderItem={renderParticipant}
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <Text style={[styles.emptyText, { color: colors.textSecondary }]}>{t('tournaments.noParticipantsYet')}</Text>
              </View>
            }
            scrollEnabled={false}
          />
        </View>

        {/* Bottom Actions - Only show View Bracket for started/completed tournaments */}
        {tournament.status !== 'setup' && (
          <View style={[styles.bottomActions, { backgroundColor: colors.surface, borderTopColor: colors.border }]}>
            <TouchableOpacity
              style={[styles.viewBracketButton, { backgroundColor: colors.primary }]}
              onPress={handleViewBracket}
            >
              <Text style={[styles.viewBracketButtonText, { color: colors.primaryText }]}>{t('tournaments.viewBracket')}</Text>
            </TouchableOpacity>
          </View>
        )}

        <AdBanner colors={colors} />
      </ScrollView>

      {/* QR Share Modal */}
      <ShareQRModal
        visible={qrModalVisible}
        onClose={() => setQrModalVisible(false)}
        url={shareUrl}
        title={tournament.name}
        colors={colors}
      />
    </PatternBackground>
  );
};
